import { FC, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { TRANCHE_REDESIGN } from 'adminConstants/routes';
import Page from 'components/new/Page';
import TabsPanel from 'further-ui/components/TabsPanel';
import TrancheEdit from './Tranche';
import DataRoom from './DataRoom';
import { TrancheData } from '../types';
import {
  PageMessagesContext,
  usePageMessagesState,
} from 'components/new/Page/usePageMessages';
import { Row } from 'further-ui/components/Layout';
import { Button } from '@mui/material';
import StatusSelect from './StatusSelect';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { FundStatus } from 'further-types/fund';
import { RolePermission } from 'further-types/admin-user-role';
import useTrancheForm, { tranchePublishableSchema } from '../useTrancheForm';
import useIsSyndicate from '../useIsSyndicate';
import { mapTrancheToFormValues } from '../mapping';
import { getHiddenFundStatuses } from './statusOptions';
import DraftTrancheBanner from './Tranche/DraftTrancheBanner';
import { TrancheDotColor } from './dotColors';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { useGetRole } from 'hooks/ui/useGetRole';

enum TrancheFormTab {
  Tranche = 'tranche',
  DataRoom = 'data-room',
}

type Props = {
  tranche?: TrancheData;
};

const TrancheFormV2: FC<Props> = ({ tranche }) => {
  const heading = tranche?.fundName ? tranche.fundName : 'Untitled tranche';
  const { messages, contextValue } = usePageMessagesState();
  const { addMessage, removeMessage } = contextValue;

  const updateStatus = api.fund.updateStatus.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche status updated successfully.',
      invalidations: [['tranches']],
    }),
  );

  const { firmId: usersFirmId } = useGetRole();
  const form = useTrancheForm({
    firmId: usersFirmId,
    values: tranche ? mapTrancheToFormValues(tranche) : undefined,
  });
  const fundStatus = form.watch('fundStatus');

  const isSyndicate = useIsSyndicate(tranche?.tag);

  const isCreating = !tranche?.id;
  const hasInvestments = (tranche?.investments ?? []).length > 0;

  const isDraftTranche = tranche?.fundStatus === FundStatus.Draft;
  const isPublishable = isDraftTranche
    ? tranchePublishableSchema.safeParse({
        ...mapTrancheToFormValues(tranche),
        isSyndicate,
      }).success
    : null;

  const [canEdit, canCreateDataRoom] = useHasPermissions([
    RolePermission.EditTranche,
    RolePermission.CreateDataRoom,
  ]);
  const history = useHistory();

  const hiddenStatuses: Array<FundStatus> = [
    ...getHiddenFundStatuses(isCreating, hasInvestments),
    ...(isPublishable === false ? [FundStatus.Open] : []),
  ];

  const persistStatus = (newStatus: FundStatus) => {
    if (!tranche?.id) return;
    updateStatus.mutate(
      { id: tranche.id, firmId: tranche.firmId, fundStatus: newStatus },
      {
        onSuccess: () => {
          form.resetField('fundStatus', { defaultValue: newStatus });
        },
      },
    );
  };

  const handleStatusChange = (newStatus: FundStatus) =>
    persistStatus(newStatus);

  const handleOpenTranche = () => persistStatus(FundStatus.Open);

  useEffect(() => {
    if (!isDraftTranche) {
      removeMessage('draft-status');
      return;
    }
    addMessage({
      key: 'draft-status',
      variant: 'neutral',
      centered: true,
      content: (
        <DraftTrancheBanner
          isPublishable={isPublishable ?? false}
          onOpenTranche={handleOpenTranche}
        />
      ),
    });
  }, [isDraftTranche, isPublishable]);

  return (
    <PageMessagesContext.Provider value={contextValue}>
      <Page
        customActions={
          <Row centered spacing="sm" fullWidth>
            <StatusSelect
              value={fundStatus}
              onChange={handleStatusChange}
              isSaving={updateStatus.isPending}
              disabled={!canEdit}
              hiddenStatuses={hiddenStatuses}
            />
            {canCreateDataRoom && !tranche?.dataRoomId && (
              <Button
                size="large"
                variant="contained"
                onClick={() => history.push(`#${TrancheFormTab.DataRoom}`)}
              >
                Add data room
              </Button>
            )}
          </Row>
        }
        messages={messages}
        heading={heading}
        goBackAction={TRANCHE_REDESIGN}
        spacing="lg"
      >
        <TabsPanel
          fullHeight
          useTabsUrls
          tabs={[
            {
              key: TrancheFormTab.Tranche,
              label: 'Tranche',
              dotColor:
                isDraftTranche && isPublishable === false
                  ? TrancheDotColor.Draft
                  : undefined,
              content: (
                <TrancheEdit
                  form={form}
                  tranche={tranche}
                  isPublishable={isPublishable}
                  isCreating={isCreating}
                  hasInvestments={hasInvestments}
                  isStatusUpdating={updateStatus.isPending}
                />
              ),
              urlParam: TrancheFormTab.Tranche,
            },
            ...(tranche && fundStatus !== FundStatus.Draft
              ? [
                  {
                    key: TrancheFormTab.DataRoom,
                    label: 'Data room',
                    content: tranche ? (
                      <DataRoom
                        setLiveDisabled={
                          fundStatus !== FundStatus.Open &&
                          fundStatus !== FundStatus.Closed
                        }
                        linkedToId={tranche.id}
                        dataRoomId={tranche.dataRoomId}
                      />
                    ) : null,
                    urlParam: TrancheFormTab.DataRoom,
                  },
                ]
              : []),
          ]}
        />
      </Page>
    </PageMessagesContext.Provider>
  );
};

export default TrancheFormV2;
